import { Jwt } from "@verity/api-client";
import apiClient from "./api-client";

export type PrivilegeName =
	| "INSTITUTE"
	| "INSTITUTE/FEATURE"
	| "ACCOUNT"
	| "USER"
	| "PRIVILEGE"
	| "PRIVILEGE_GROUP"
	| "LIVE_SESSION"
	| "LIVE_PROCTOR_GROUP"
	| "LIVE_PROCTOR_GROUP/ADMIN"
	| "INSTITUTE_REPORT"
	| "COURSE_REPORT"
	| "ASSESSMENTS"
	| "PROCTORING_RESULTS"
	| "LTI1P3_CLIENT_REGISTRATION"
	| "COURSE"
	| "API_KEY"
	| "DEPLOYMENT"
	| "RELYING_PARTY_REGISTRATION"
	| "AUDIT_LOG"
	| "FEEDBACK"
	| "EVENT_BANNER"
	| "APPLICATION";
export type ActionName = "CREATE" | "READ" | "UPDATE" | "DELETE";

const ACTION_CONVERTOR: ActionName[] = ["CREATE", "READ", "UPDATE", "DELETE"];

const NON_INSTITUTE_SCOPED_RESOURCES = new Set<PrivilegeName>(["ACCOUNT"]);

export type UserPrivileges = Exclude<Awaited<ReturnType<typeof apiClient.permission.getUserPrivileges>>, null>;
export type PrivilegeScope = UserPrivileges[string][number];

export type PrivilegeSpecification = {
	privilege: PrivilegeName;
	action?: ActionName;
};

export function convertJwtPrivilegeScopes(jwt: Jwt): Record<string, PrivilegeScope[]> {
	return Object.fromEntries(
		Object.entries(jwt.sps ?? {}).map(([privilege, scopes]) => [
			privilege,
			scopes.map(({ iid, cid, rid }) => ({
				instituteId: iid != null ? jwt.iids?.[iid] : undefined,
				courseId: cid != null ? jwt.cids?.[cid] : undefined,
				resourceId: rid != null ? jwt.rids?.[rid] : undefined,
			})),
		]),
	);
}

function getFlattenedPrivileges(privileges: UserPrivileges, { privilege, action }: PrivilegeSpecification) {
	if (action) {
		return privileges[`${privilege}:${ACTION_CONVERTOR.indexOf(action)}`] ?? [];
	} else {
		return Object.entries(privileges)
			.filter(([key]) => key.startsWith(`${privilege}:`))
			.flatMap(([, scopes]) => scopes);
	}
}

export function canAccessSome(userInfo: { privileges?: UserPrivileges } | undefined, spec: PrivilegeSpecification) {
	const scopes = getFlattenedPrivileges(userInfo?.privileges ?? {}, spec);
	return scopes.length > 0;
}

export function canAccessResource(
	userInfo: { privileges?: UserPrivileges } | undefined,
	spec: PrivilegeSpecification & { resourceScope: PrivilegeScope },
) {
	const { resourceScope } = spec;
	const scopes = getFlattenedPrivileges(userInfo?.privileges ?? {}, spec);
	return scopes.some((scope) => {
		if (scope.resourceId) {
			return resourceScope.resourceId === scope.resourceId;
		}
		if (scope.courseId) {
			return resourceScope.courseId === scope.courseId;
		}
		if (scope.instituteId && !NON_INSTITUTE_SCOPED_RESOURCES.has(spec.privilege)) {
			return resourceScope.instituteId === scope.instituteId;
		}
		return true;
	});
}

export function getAllowableInstitutes(
	userInfo: { privileges?: UserPrivileges } | undefined,
	spec: PrivilegeSpecification,
): Set<string> | "all" {
	if (canAccessResource(userInfo, { ...spec, resourceScope: {} })) {
		return "all";
	}
	const institutes = new Set<string>();
	getFlattenedPrivileges(userInfo?.privileges ?? {}, spec).forEach(({ instituteId }) => {
		if (instituteId) {
			institutes.add(instituteId);
		}
	});
	return institutes;
}
